import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as rolesApi from './roles.api';

import type {
  CreateRoleRequest,
  RoleListQuery,
  SetRolePermissionsRequest,
  UpdateRoleRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: RoleListQuery) => ['roles', 'list', query] as const,
  detail: (id: string) => ['roles', 'detail', id] as const,
  registry: () => ['roles', 'registry'] as const,
};

export const useRolesList = (query: RoleListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => rolesApi.listRoles(query),
    staleTime: 30_000,
  });

export const useRoleDetail = (id: string | undefined) =>
  useQuery({
    queryKey: QK.detail(id ?? ''),
    queryFn: () => rolesApi.getRole(id!),
    enabled: !!id,
  });

/**
 * Module keys hidden from the permission matrix UI only. The backend still
 * serves these in the registry and continues to define + enforce their codes;
 * this purely drops the rows from the role editor and the user-permissions
 * panel so operators don't see them.
 */
const HIDDEN_MODULE_KEYS = new Set(['ops.scheduler', 'ops.status']);

export const usePermissionRegistry = () =>
  useQuery({
    queryKey: QK.registry(),
    queryFn: () => rolesApi.getPermissionRegistry(),
    staleTime: 5 * 60_000,
    select: (data) => ({
      ...data,
      modules: data.modules.filter((m) => !HIDDEN_MODULE_KEYS.has(m.moduleKey)),
    }),
  });

const extractMessage = (err: unknown): string | undefined => {
  if (!err || typeof err !== 'object') return undefined;
  const e = err as { response?: { data?: { message?: string | string[] } }; message?: string };
  const m = e.response?.data?.message;
  if (Array.isArray(m)) return m.join('; ');
  return m ?? e.message;
};

export const useCreateRole = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateRoleRequest) => rolesApi.createRole(payload),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['roles', 'list'] });
      toast.success(`Role ${r.name} created`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to create role'),
  });
};

export const useUpdateRole = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateRoleRequest }) =>
      rolesApi.updateRole(id, payload),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['roles', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(r.id) });
      toast.success('Role updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update role'),
  });
};

export const useDeleteRole = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rolesApi.deleteRole(id),
    onSuccess: (_v, id) => {
      qc.invalidateQueries({ queryKey: ['roles', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(id) });
      toast.success('Role deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete role'),
  });
};

export const useSetRolePermissions = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: SetRolePermissionsRequest }) =>
      rolesApi.setRolePermissions(id, payload),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['roles', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(r.id) });
      // The caller's own effective permission set may have changed (the
      // matrix could be editing their own role or an ancestor of it).
      // Re-fetch /me so the sidebar + route guards pick up the new set
      // without forcing a re-login.
      qc.invalidateQueries({ queryKey: ['me', 'profile'] });
      // Success toast is fired by the caller (PermissionMatrix) so the
      // message can include the grant / revoke diff. We only handle errors
      // here so every caller of this mutation still gets a failure toast.
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to save permissions'),
  });
};
